/**
 * =====================================================================
 * TdsFormNext 标准字段池 (SCHEMA_STANDARD_FIELD_POOL)
 * =====================================================================
 *
 * 【重要】这是 TdsFormNext **新协议** 专用的标准字段池。
 *
 * 路径: tds-form-next/standard-field-pool.ts
 * 导出: SCHEMA_STANDARD_FIELD_POOL
 *
 * ⚠️ 注意区分：
 *   - ✅ 新协议: tds-form-next/standard-field-pool.ts → SCHEMA_STANDARD_FIELD_POOL
 *   - ❌ 老协议: tds-form/tds-schema-form/standard-field-pool.ts → STANDARD_FIELD_POOL (已废弃)
 *
 * 新协议字段结构特点：
 *   - 使用 componentProps.type 指定组件类型 (如 'tag', 'dateCascader', 'custom')
 *   - 使用 fieldProps.filterName 指定过滤器字段名
 *   - 使用 TdsFromNextGroup 枚举指定分组
 *
 * 标准字段名枚举来自 StandardCommonFieldKey，例如：
 *   - StandardCommonFieldKey.TimeRange → 'standard_time_range'
 *   - StandardCommonFieldKey.AppVersion → 'standard_app_version'
 *   - StandardCommonFieldKey.SdkVersion → 'standard_sdk_version'
 *   - StandardCommonFieldKey.Model → 'standard_model'
 *   - 等等...
 *
 * 配合 prompt.md 使用，用于 Schema 协议生成。
 * =====================================================================
 */
import i18n from '@/i18n';
import { CHARACTER_MATCH_OPTIONS, MatchMode, NUMBER_MATCH_OPTIONS, SHOW_NEW_MATCH_OPTIONS, STACK_CHARACTER_MATCH_OPTIONS, TEXT_MATCH_OPTIONS, VERSION_MATCH_OPTIONS } from '@/presentation/components/form/standards/types';
import { COMMON_TIME_TYPE_OPTIONS, ISSUE_TIME_TYPE } from '@/presentation/components/tds-form/tds-date-cascader/config';
import { CommonFieldKey, FieldKey, StandardCommonFieldKey, SystemCustomKey, Zone } from '@/proto/field';

import { stringFilterTips } from '../issue-list/components/tips';
import {
  dateCascaderConverter, dateCascaderTransformer, KeyDataType } from '../tds-form/utils';
import { TdsFromNextGroup } from './components/field-filter-panel/config';
import { ComponentType, SchemaFormItem } from './protocol';
import { tagFilterValue2ScaledFilter, UNIT_SCALE } from './utils';

const DEFAULT_STANDARD_FIELD_MAX_WIDTH = '200px';
const SHOW_NEW_OPERATOR_MAP: Record<string, boolean> = { [MatchMode.NOTEMPTY]: true, [MatchMode.ISEMPTY]: false };

const RAW_SCHEMA_STANDARD_FIELD_POOL: Record<string, SchemaFormItem> = {
  [StandardCommonFieldKey.TimeRange]: {
    name: StandardCommonFieldKey.TimeRange,
    label: i18n.t('时间范围'),
    attach: true,
    hidden: false,
    order: 99,
    formItemProps: {
      style: { maxWidth: '558px' },
      convertValue: dateCascaderConverter,
      transform: dateCascaderTransformer,
    },
    componentProps: {
      type: 'dateCascader' as ComponentType,
      maxLimitedDays: 90,
      timeWindowOptions: ISSUE_TIME_TYPE,
      timeTypeOptions: COMMON_TIME_TYPE_OPTIONS,
    },
    fieldProps: {
      filterName: 'event_time',
    },
  },

  [StandardCommonFieldKey.AbTestTag]: {
    name: StandardCommonFieldKey.AbTestTag,
    label: i18n.t('业务下钻'),
    attach: false,
    hidden: false,
    formItemProps: {
      style: { maxWidth: DEFAULT_STANDARD_FIELD_MAX_WIDTH },
    },
    componentProps: {
      type: 'tag' as ComponentType,
      operatorOptions: CHARACTER_MATCH_OPTIONS,
      placeholder: i18n.t('请选择'),
    },
    fieldProps: {
      fieldKey: 'ab_test_tag', // 用于远程请求下拉框时用的eky
      filterKey: SystemCustomKey.AbTestTag, // 用于转换到filter里用的key
      filterName: CommonFieldKey.ShiplyAbTestTag,
      keyDataType: KeyDataType.ARRAY,
    },
    group: TdsFromNextGroup.Custom,
  },

  [StandardCommonFieldKey.ShiplyAbTestTag]: {
    name: StandardCommonFieldKey.AbTestTag,
    label: i18n.t('实验ID'),
    attach: false,
    hidden: false,
    formItemProps: {
      style: { maxWidth: DEFAULT_STANDARD_FIELD_MAX_WIDTH },
    },
    componentProps: {
      type: 'tag' as ComponentType,
      operatorOptions: CHARACTER_MATCH_OPTIONS,
      placeholder: i18n.t('请选择'),
    },
    fieldProps: {
      filterTransform: (_fieldKey: string, fieldValue: any) => fieldValue?.conditions?.map((item: any) => ({
        name: 'shiply_ab_test_tag',
        operator: item.operator,
        values: item.values || [],
        key: SystemCustomKey.ShiplyTag,
        type: 2,
      })) ?? [],
      filterName: CommonFieldKey.ShiplyTag,
    },
    // Shiply 实验 ID 也是平台维度标签，放到「平台标签」
    group: TdsFromNextGroup.Custom,
  },

  [StandardCommonFieldKey.TagsInFilter]: {
    name: StandardCommonFieldKey.TagsInFilter,
    label: i18n.t('个例标签'),
    attach: false,
    hidden: false,
    formItemProps: {
      style: { maxWidth: DEFAULT_STANDARD_FIELD_MAX_WIDTH },
    },
    componentProps: {
      type: 'tag' as ComponentType,
      operatorOptions: CHARACTER_MATCH_OPTIONS,
      placeholder: i18n.t('请选择'),
    },
    fieldProps: {
      filterName: CommonFieldKey.TagsInFilter,
      keyDataType: KeyDataType.ARRAY,
      filterKey: SystemCustomKey.TagsInFilter,
    },
    group: TdsFromNextGroup.Tag,
  },

  [StandardCommonFieldKey.AccountId]: {
    name: StandardCommonFieldKey.AccountId,
    label: i18n.t('用户ID'),
    attach: false,
    hidden: false,
    formItemProps: {
      style: { maxWidth: DEFAULT_STANDARD_FIELD_MAX_WIDTH },
    },
    componentProps: {
      type: 'tag' as ComponentType,
      operatorOptions: CHARACTER_MATCH_OPTIONS,
      placeholder: i18n.t('请输入用户ID'),
    },
    fieldProps: {
      fieldScope: null,
      filterName: CommonFieldKey.AccountId,
    },
    group: TdsFromNextGroup.UserInfo,
  },

  [StandardCommonFieldKey.Qimei]: {
    name: StandardCommonFieldKey.Qimei,
    label: i18n.t('设备ID'),
    attach: false,
    hidden: false,
    formItemProps: {
      style: { maxWidth: DEFAULT_STANDARD_FIELD_MAX_WIDTH },
    },
    componentProps: {
      type: 'tag' as ComponentType,
      operatorOptions: CHARACTER_MATCH_OPTIONS,
      placeholder: i18n.t('请输入设备ID'),
    },
    fieldProps: {
      fieldScope: null,
      filterName: CommonFieldKey.Qimei,
    },
    group: TdsFromNextGroup.DeviceInfo,
  },

  [StandardCommonFieldKey.BundleId]: {
    name: StandardCommonFieldKey.BundleId,
    label: 'Bundle ID',
    attach: false,
    hidden: false,
    formItemProps: {
      style: { maxWidth: DEFAULT_STANDARD_FIELD_MAX_WIDTH },
    },
    componentProps: {
      type: 'tag' as ComponentType,
      operatorOptions: CHARACTER_MATCH_OPTIONS,
      placeholder: i18n.t('请输入 Bundle ID'),
    },
    fieldProps: {
      filterName: CommonFieldKey.BundleId,
    },
    batchEditMode: true,
    batchEditTips: stringFilterTips,
    group: TdsFromNextGroup.ApplicationInfo,
  },

  [StandardCommonFieldKey.AppVersion]: {
    name: StandardCommonFieldKey.AppVersion,
    label: i18n.t('APP版本'),
    attach: false,
    hidden: false,
    batchEditMode: true,
    batchEditTips: stringFilterTips,
    formItemProps: {
      style: { maxWidth: DEFAULT_STANDARD_FIELD_MAX_WIDTH },
    },
    componentProps: {
      type: 'tag' as ComponentType,
      operatorOptions: VERSION_MATCH_OPTIONS,
      placeholder: i18n.t('请选择'),
    },
    fieldProps: {
      filterName: CommonFieldKey.AppVersion,
    },
    order: 2,
    group: TdsFromNextGroup.ApplicationInfo,
  },

  [StandardCommonFieldKey.BuildNumber]: {
    name: StandardCommonFieldKey.BuildNumber,
    label: i18n.t('构建号'),
    attach: false,
    hidden: false,
    formItemProps: {
      style: { maxWidth: DEFAULT_STANDARD_FIELD_MAX_WIDTH },
    },
    batchEditMode: true,
    batchEditTips: stringFilterTips,
    componentProps: {
      type: 'tag' as ComponentType,
      operatorOptions: CHARACTER_MATCH_OPTIONS,
      placeholder: i18n.t('请输入构建号'),
    },
    fieldProps: {
      filterName: CommonFieldKey.BuildNumber,
    },
    group: TdsFromNextGroup.ApplicationInfo,
  },

  [StandardCommonFieldKey.AppVersionMode]: {
    name: StandardCommonFieldKey.AppVersionMode,
    label: i18n.t('构建类型'),
    attach: false,
    hidden: false,
    formItemProps: {
      style: { maxWidth: DEFAULT_STANDARD_FIELD_MAX_WIDTH },
    },
    componentProps: {
      type: 'tag' as ComponentType,
      operatorOptions: CHARACTER_MATCH_OPTIONS,
      placeholder: i18n.t('请选择'),
      options: [
        { value: 'Unknown', label: i18n.t('未知') },
        { value: 'Debug', label: i18n.t('开发版') },
        { value: 'Gray', label: i18n.t('灰度版') },
        { value: 'Release', label: i18n.t('正式版') },
      ],
    },
    fieldProps: {
      filterName: CommonFieldKey.AppVersionMode,
      fieldScope: null,
    },
    group: TdsFromNextGroup.ApplicationInfo,
  },

  [StandardCommonFieldKey.SdkVersion]: {
    name: StandardCommonFieldKey.SdkVersion,
    label: i18n.t('SDK版本'),
    attach: false,
    hidden: false,
    formItemProps: {
      style: { maxWidth: DEFAULT_STANDARD_FIELD_MAX_WIDTH },
    },
    componentProps: {
      type: 'tag' as ComponentType,
      operatorOptions: VERSION_MATCH_OPTIONS,
      placeholder: i18n.t('请选择'),
    },
    fieldProps: {
      filterName: CommonFieldKey.SdkVersion,
    },
    batchEditMode: true,
    batchEditTips: stringFilterTips,
    group: TdsFromNextGroup.ApplicationInfo,
  },

  [StandardCommonFieldKey.ChannelId]: {
    name: StandardCommonFieldKey.ChannelId,
    label: i18n.t('渠道'),
    attach: false,
    hidden: false,
    formItemProps: {
      style: { maxWidth: DEFAULT_STANDARD_FIELD_MAX_WIDTH },
    },
    componentProps: {
      type: 'tag' as ComponentType,
      operatorOptions: CHARACTER_MATCH_OPTIONS,
      placeholder: i18n.t('请输入'),
    },
    fieldProps: {
      filterName: CommonFieldKey.ChannelId,
    },
    batchEditMode: true,
    batchEditTips: stringFilterTips,
    group: TdsFromNextGroup.ApplicationInfo,
  },

  [StandardCommonFieldKey.Model]: {
    name: StandardCommonFieldKey.Model,
    label: i18n.t('机型'),
    attach: false,
    hidden: false,
    formItemProps: {
      style: { maxWidth: DEFAULT_STANDARD_FIELD_MAX_WIDTH },
    },
    componentProps: {
      type: 'modelTag' as ComponentType,
      operatorOptions: CHARACTER_MATCH_OPTIONS,
      placeholder: i18n.t('请选择'),
    },
    fieldProps: {
      filterName: CommonFieldKey.Model,
    },
    batchEditMode: true,
    batchEditTips: stringFilterTips,
    // 机型是设备属性，归为「设备信息」
    group: TdsFromNextGroup.DeviceInfo,
  },

  [StandardCommonFieldKey.OsVersion]: {
    name: StandardCommonFieldKey.OsVersion,
    label: i18n.t('系统版本'),
    attach: false,
    hidden: false,
    formItemProps: {
      style: { maxWidth: DEFAULT_STANDARD_FIELD_MAX_WIDTH },
    },
    componentProps: {
      type: 'tag' as ComponentType,
      operatorOptions: VERSION_MATCH_OPTIONS,
      placeholder: i18n.t('请选择'),
    },
    fieldProps: {
      filterName: CommonFieldKey.OsVersion,
    },
    batchEditMode: true,
    batchEditTips: stringFilterTips,
    group: TdsFromNextGroup.DeviceInfo,
  },

  [StandardCommonFieldKey.ProcessName]: {
    name: StandardCommonFieldKey.ProcessName,
    label: i18n.t('进程'),
    attach: false,
    hidden: false,
    formItemProps: {
      style: { maxWidth: DEFAULT_STANDARD_FIELD_MAX_WIDTH },
    },
    componentProps: {
      type: 'tag' as ComponentType,
      operatorOptions: [
        { value: MatchMode.MATCH, label: i18n.t('匹配') },
        ...CHARACTER_MATCH_OPTIONS.filter(opt => opt.value !== MatchMode.MATCH),
      ],
      placeholder: i18n.t('请输入进程名'),
    },
    fieldProps: {
      filterName: CommonFieldKey.ProcessName,
    },
    batchEditMode: true,
    batchEditTips: stringFilterTips,
    group: TdsFromNextGroup.ExceptionInfo,
  },

  [StandardCommonFieldKey.MonitoredThreadName]: {
    name: StandardCommonFieldKey.MonitoredThreadName,
    label: i18n.t('监控线程'),
    attach: false,
    hidden: false,
    formItemProps: {
      style: { maxWidth: DEFAULT_STANDARD_FIELD_MAX_WIDTH },
    },
    componentProps: {
      type: 'tag' as ComponentType,
      operatorOptions: CHARACTER_MATCH_OPTIONS,
      placeholder: i18n.t('请输入监控线程名'),
    },
    fieldProps: {
      filterName: CommonFieldKey.MonitoredThreadName,
    },
    group: TdsFromNextGroup.ExceptionInfo,
  },

  [StandardCommonFieldKey.SystemStack]: {
    name: StandardCommonFieldKey.SystemStack,
    label: i18n.t('系统堆栈'),
    attach: false,
    hidden: false,
    formItemProps: {
      style: { maxWidth: DEFAULT_STANDARD_FIELD_MAX_WIDTH },
    },
    componentProps: {
      type: 'tag' as ComponentType,
      operatorOptions: CHARACTER_MATCH_OPTIONS,
      placeholder: i18n.t('请选择'),
    },
    fieldProps: {
      filterName: CommonFieldKey.SystemStack,
    },
    group: TdsFromNextGroup.ExceptionInfo,
  },

  [StandardCommonFieldKey.LlmModel]: {
    name: StandardCommonFieldKey.LlmModel,
    label: i18n.t('模型'),
    attach: false,
    hidden: false,
    formItemProps: {
      style: { maxWidth: DEFAULT_STANDARD_FIELD_MAX_WIDTH },
    },
    componentProps: {
      type: 'tag' as ComponentType,
      operatorOptions: CHARACTER_MATCH_OPTIONS,
      placeholder: i18n.t('请选择'),
    },
    fieldProps: {
      filterName: FieldKey.LlmModel,
    },
    group: TdsFromNextGroup.ContextInfo,
  },

  [StandardCommonFieldKey.SceneName]: {
    name: StandardCommonFieldKey.SceneName,
    label: i18n.t('场景'),
    attach: false,
    hidden: false,
    formItemProps: {
      style: { maxWidth: DEFAULT_STANDARD_FIELD_MAX_WIDTH },
    },
    componentProps: {
      type: 'tag' as ComponentType,
      operatorOptions: CHARACTER_MATCH_OPTIONS,
      placeholder: i18n.t('请选择'),
    },
    fieldProps: {
      filterName: CommonFieldKey.SceneName,
    },
    batchEditMode: true,
    batchEditTips: stringFilterTips,
    group: TdsFromNextGroup.ContextInfo,
  },

  [StandardCommonFieldKey.GraphicLevelMask]: {
    name: StandardCommonFieldKey.GraphicLevelMask,
    label: i18n.t('画质'),
    attach: false,
    hidden: false,
    formItemProps: {
      style: { maxWidth: DEFAULT_STANDARD_FIELD_MAX_WIDTH },
    },
    componentProps: {
      type: 'tag' as ComponentType,
      placeholder: i18n.t('请选择'),
      operatorOptions: CHARACTER_MATCH_OPTIONS,
      keyOptions: [
        { label: i18n.t('抗锯齿等级'), value: 'anti_aliasing_level' },
        { label: i18n.t('设备等级'), value: 'device_level' },
        { label: i18n.t('FPS等级'), value: 'fps_level' },
        { label: i18n.t('质量等级'), value: 'quality_level' },
        { label: i18n.t('分辨率等级'), value: 'resolution_level' },
      ],
    },
    fieldProps: {
      // 显式禁用远程候选值加载，并阻止 zone 兜底注入 fieldScope
      fieldScope: null,
      keyDataType: KeyDataType.MAP,
      filterName: CommonFieldKey.GraphicLevelMap,
    },
    group: TdsFromNextGroup.ContextInfo,
  },

  [StandardCommonFieldKey.NetWorkType]: {
    name: StandardCommonFieldKey.NetWorkType,
    label: i18n.t('网络类型'),
    attach: false,
    hidden: false,
    formItemProps: {
      style: { maxWidth: DEFAULT_STANDARD_FIELD_MAX_WIDTH },
    },
    componentProps: {
      type: 'tag' as ComponentType,
      operatorOptions: CHARACTER_MATCH_OPTIONS,
      placeholder: i18n.t('请选择'),
    },
    fieldProps: {
      filterName: CommonFieldKey.NetworkType,
    },
    group: TdsFromNextGroup.ContextInfo,
  },

  [StandardCommonFieldKey.Span]: {
    name: StandardCommonFieldKey.Span,
    label: i18n.t('Span名'),
    attach: false,
    hidden: false,
    formItemProps: {
      style: { maxWidth: DEFAULT_STANDARD_FIELD_MAX_WIDTH },
    },
    componentProps: {
      type: 'tag' as ComponentType,
      operatorOptions: CHARACTER_MATCH_OPTIONS,
      placeholder: i18n.t('请选择'),
    },
    fieldProps: {
      filterName: CommonFieldKey.Span,
    },
    group: TdsFromNextGroup.ContextInfo,
  },

  [StandardCommonFieldKey.Area]: {
    name: StandardCommonFieldKey.Area,
    label: i18n.t('地域'),
    attach: false,
    hidden: false,
    formItemProps: {
      style: { maxWidth: DEFAULT_STANDARD_FIELD_MAX_WIDTH },
    },
    componentProps: {
      type: 'area' as ComponentType,
    },
    fieldProps: {
      filterName: 'area',
      filterTransform: (_fieldKey: string, fieldValue: any) => fieldValue?.map((item: any) => ({
        name: item.name,
        operator: item.operator,
        values: item.values || [],
      })) ?? [],
    },
    group: TdsFromNextGroup.UserInfo,
  },
  [StandardCommonFieldKey.Stage]: {
    name: StandardCommonFieldKey.Stage,
    label: i18n.t('场景'),
    attach: false,
    hidden: false,
    formItemProps: {
      style: { maxWidth: DEFAULT_STANDARD_FIELD_MAX_WIDTH },
    },
    componentProps: {
      type: 'tag' as ComponentType,
      operatorOptions: CHARACTER_MATCH_OPTIONS,
      placeholder: i18n.t('请选择'),
    },
    fieldProps: {
      filterName: CommonFieldKey.Stage,
    },
    batchEditMode: true,
    batchEditTips: stringFilterTips,
    group: TdsFromNextGroup.ContextInfo,
  },

  [StandardCommonFieldKey.FpsAvg]: {
    name: StandardCommonFieldKey.FpsAvg,
    label: i18n.t('FPS均值'),
    attach: false,
    hidden: false,
    formItemProps: {
      style: { maxWidth: DEFAULT_STANDARD_FIELD_MAX_WIDTH },
    },
    componentProps: {
      type: 'tag' as ComponentType,
      operatorOptions: NUMBER_MATCH_OPTIONS,
      placeholder: i18n.t('请输入FPS均值'),
    },
    fieldProps: {
      filterName: CommonFieldKey.FpsAvg,
    },
    group: TdsFromNextGroup.ContextInfo,
  },

  [StandardCommonFieldKey.FpsStutter]: {
    name: StandardCommonFieldKey.FpsStutter,
    label: i18n.t('FPS卡顿'),
    attach: false,
    hidden: false,
    formItemProps: {
      style: { maxWidth: DEFAULT_STANDARD_FIELD_MAX_WIDTH },
    },
    componentProps: {
      type: 'tag' as ComponentType,
      operatorOptions: NUMBER_MATCH_OPTIONS,
      placeholder: i18n.t('请输入FPS卡顿值'),
    },
    fieldProps: {
      filterName: CommonFieldKey.FpsStutter,
    },
    group: TdsFromNextGroup.ContextInfo,
  },

  [StandardCommonFieldKey.OneLowFrameBase]: {
    name: StandardCommonFieldKey.OneLowFrameBase,
    label: i18n.t('1%Low帧(Frame-base)'),
    attach: false,
    hidden: false,
    formItemProps: {
      style: { maxWidth: DEFAULT_STANDARD_FIELD_MAX_WIDTH },
    },
    componentProps: {
      type: 'tag' as ComponentType,
      operatorOptions: NUMBER_MATCH_OPTIONS,
      placeholder: i18n.t('请输入1%Low帧'),
    },
    fieldProps: {
      filterName: CommonFieldKey.OneLowFrameBase,
    },
    group: TdsFromNextGroup.ContextInfo,
  },

  [StandardCommonFieldKey.OneLowTimeBase]: {
    name: StandardCommonFieldKey.OneLowTimeBase,
    label: i18n.t('1%Low帧(Time-base)'),
    attach: false,
    hidden: false,
    formItemProps: {
      style: { maxWidth: DEFAULT_STANDARD_FIELD_MAX_WIDTH },
    },
    componentProps: {
      type: 'tag' as ComponentType,
      operatorOptions: NUMBER_MATCH_OPTIONS,
      placeholder: i18n.t('请输入1%Low帧'),
    },
    fieldProps: {
      filterName: CommonFieldKey.OneLowTimeBase,
    },
    group: TdsFromNextGroup.ContextInfo,
  },

  [StandardCommonFieldKey.JankCount]: {
    name: StandardCommonFieldKey.JankCount,
    label: i18n.t('Jank次数'),
    attach: false,
    hidden: false,
    formItemProps: {
      style: { maxWidth: DEFAULT_STANDARD_FIELD_MAX_WIDTH },
    },
    componentProps: {
      type: 'tag' as ComponentType,
      operatorOptions: NUMBER_MATCH_OPTIONS,
      placeholder: i18n.t('请输入Jank次数'),
    },
    fieldProps: {
      filterName: CommonFieldKey.JankCount,
    },
    group: TdsFromNextGroup.ContextInfo,
  },

  [StandardCommonFieldKey.BigJankCount]: {
    name: StandardCommonFieldKey.BigJankCount,
    label: i18n.t('BigJank次数'),
    attach: false,
    hidden: false,
    formItemProps: {
      style: { maxWidth: DEFAULT_STANDARD_FIELD_MAX_WIDTH },
    },
    componentProps: {
      type: 'tag' as ComponentType,
      operatorOptions: NUMBER_MATCH_OPTIONS,
      placeholder: i18n.t('请输入BigJank次数'),
    },
    fieldProps: {
      filterName: CommonFieldKey.BigJankCount,
    },
    group: TdsFromNextGroup.ContextInfo,
  },

  standard_is64bit: {
    name: 'standard_is64bit',
    label: i18n.t('32/64位'),
    attach: false,
    hidden: false,
    formItemProps: {
      style: { maxWidth: DEFAULT_STANDARD_FIELD_MAX_WIDTH },
    },
    componentProps: {
      type: 'tag' as ComponentType,
      operatorOptions: CHARACTER_MATCH_OPTIONS,
      placeholder: i18n.t('请选择'),
      options: [
        { value: '64bit', label: i18n.t('64位') },
        { value: '32bit', label: i18n.t('32位') },
      ],
    },
    fieldProps: {
      filterName: FieldKey.Bit,
      fieldKey: FieldKey.Bit,
      keyDataType: KeyDataType.STRING,
    },
    group: TdsFromNextGroup.ContextInfo,
  },

  standard_show_new: {
    name: 'standard_show_new',
    label: i18n.t('只显示新增问题'),
    attach: false,
    hidden: true,
    hiddenWhenCompareMode: true,
    order: 1,
    formItemProps: {
      style: { maxWidth: '300px' },
    },
    componentProps: {
      type: 'tag' as ComponentType,
      operatorOptions: SHOW_NEW_MATCH_OPTIONS,
      placeholder: i18n.t('请选择'),
      defaultValue: MatchMode.ISEMPTY,
    },
    fieldProps: {
      fieldScope: null,
      filterName: FieldKey.ShowNew,
      excludeFromFilters: true,
      valueTransform: (value: any) => ({
        showNew: SHOW_NEW_OPERATOR_MAP[value?.conditions?.[0]?.operator],
      }),
    },
    group: TdsFromNextGroup.ExceptionInfo,
  },

  standard_suspicious_count: {
    name: 'standard_suspicious_count',
    label: i18n.t('疑似泄漏FD数量'),
    attach: false,
    hidden: false,
    formItemProps: {
      style: { maxWidth: DEFAULT_STANDARD_FIELD_MAX_WIDTH },
    },
    componentProps: {
      type: 'tag' as ComponentType,
      operatorOptions: NUMBER_MATCH_OPTIONS,
      placeholder: i18n.t('请输入'),
    },
    fieldProps: {
      fieldScope: null,
      filterName: 'suspicious_count',
    },
    group: TdsFromNextGroup.ExceptionInfo,
  },

  standard_fd_type: {
    name: 'standard_fd_type',
    label: i18n.t('FD类型'),
    attach: false,
    hidden: false,
    formItemProps: {
      style: { maxWidth: DEFAULT_STANDARD_FIELD_MAX_WIDTH },
    },
    componentProps: {
      type: 'tag' as ComponentType,
      operatorOptions: CHARACTER_MATCH_OPTIONS,
      placeholder: i18n.t('请选择'),
      options: [
        { value: 'eventfd', label: i18n.t('eventfd（线程）') },
        { value: 'file', label: i18n.t('file（业务文件）') },
        { value: 'system_file', label: i18n.t('system（系统文件）') },
        { value: 'gpu', label: i18n.t('gpu（GPU文件）') },
        { value: 'dmabuf', label: i18n.t('dmabuf（图形缓冲区）') },
        { value: 'ashmem', label: i18n.t('ashmem（匿名内存）') },
        { value: 'device', label: i18n.t('device（设备文件）') },
        { value: 'socket', label: 'socket（Socket）' },
        { value: 'pipe', label: i18n.t('pipe（管道文件）') },
        { value: 'others', label: i18n.t('others（其他）') },
      ],
    },
    fieldProps: {
      fieldScope: null,
      filterName: FieldKey.FdType,
      fieldKey: FieldKey.FdType,
      keyDataType: KeyDataType.STRING,
    },
    group: TdsFromNextGroup.ContextInfo,
  },

  [StandardCommonFieldKey.PssStage]: {
    name: StandardCommonFieldKey.PssStage,
    label: i18n.t('场景'),
    attach: false,
    hidden: false,
    formItemProps: {
      style: { maxWidth: DEFAULT_STANDARD_FIELD_MAX_WIDTH },
    },
    componentProps: {
      type: 'tag' as ComponentType,
      operatorOptions: CHARACTER_MATCH_OPTIONS,
      placeholder: i18n.t('请输入场景'),
    },
    fieldProps: {
      filterName: CommonFieldKey.PssStage,
      fieldKey: CommonFieldKey.PssStage,
    },
    batchEditMode: true,
    batchEditTips: stringFilterTips,
    group: TdsFromNextGroup.ContextInfo,
  },

  [StandardCommonFieldKey.PssMemPeak]: {
    name: StandardCommonFieldKey.PssMemPeak,
    label: i18n.t('PSS内存峰值'),
    attach: false,
    hidden: false,
    formItemProps: {
      style: { maxWidth: DEFAULT_STANDARD_FIELD_MAX_WIDTH },
    },
    componentProps: {
      type: 'tag' as ComponentType,
      operatorOptions: NUMBER_MATCH_OPTIONS,
      placeholder: i18n.t('请输入PSS内存峰值'),
    },
    fieldProps: {
      filterName: CommonFieldKey.PssMemPeak,
    },
    group: TdsFromNextGroup.ContextInfo,
  },
  [StandardCommonFieldKey.Brand]: {
    name: StandardCommonFieldKey.Brand,
    label: i18n.t('厂商'),
    attach: false,
    hidden: false,
    formItemProps: {
      style: { maxWidth: DEFAULT_STANDARD_FIELD_MAX_WIDTH },
    },
    componentProps: {
      type: 'tag' as ComponentType,
      operatorOptions: CHARACTER_MATCH_OPTIONS,
      placeholder: i18n.t('请选择'),
    },
    fieldProps: {
      filterName: CommonFieldKey.Brand,
    },
    batchEditMode: true,
    batchEditTips: stringFilterTips,
    group: TdsFromNextGroup.DeviceInfo,
  },

  [StandardCommonFieldKey.Rom]: {
    name: StandardCommonFieldKey.Rom,
    label: 'ROM',
    attach: false,
    hidden: false,
    formItemProps: {
      style: { maxWidth: DEFAULT_STANDARD_FIELD_MAX_WIDTH },
    },
    componentProps: {
      type: 'tag' as ComponentType,
      operatorOptions: CHARACTER_MATCH_OPTIONS,
      placeholder: i18n.t('请选择'),
    },
    fieldProps: {
      filterName: CommonFieldKey.Rom,
      fieldKey: FieldKey.Rom,
    },
    group: TdsFromNextGroup.DeviceInfo,
  },

  [StandardCommonFieldKey.LaunchId]: {
    name: StandardCommonFieldKey.LaunchId,
    label: i18n.t('启动ID'),
    attach: false,
    hidden: false,
    formItemProps: {
      style: { maxWidth: DEFAULT_STANDARD_FIELD_MAX_WIDTH },
    },
    componentProps: {
      type: 'tag' as ComponentType,
      operatorOptions: CHARACTER_MATCH_OPTIONS,
      placeholder: i18n.t('请输入'),
    },
    fieldProps: {
      filterName: CommonFieldKey.LaunchId,
      fieldScope: null,
    },
    batchEditMode: true,
    batchEditTips: stringFilterTips,
    group: TdsFromNextGroup.ContextInfo,
  },

  [StandardCommonFieldKey.ProcessLaunchId]: {
    name: StandardCommonFieldKey.ProcessLaunchId,
    label: i18n.t('进程启动ID'),
    attach: false,
    hidden: false,
    formItemProps: {
      style: { maxWidth: DEFAULT_STANDARD_FIELD_MAX_WIDTH },
    },
    componentProps: {
      type: 'tag' as ComponentType,
      operatorOptions: CHARACTER_MATCH_OPTIONS,
      placeholder: i18n.t('请输入'),
    },
    fieldProps: {
      fieldScope: null,
      filterName: CommonFieldKey.ProcessLaunchId,
    },
    batchEditMode: true,
    batchEditTips: stringFilterTips,
    group: TdsFromNextGroup.ContextInfo,
  },

  [StandardCommonFieldKey.StandardLaunchDuration]: {
    name: StandardCommonFieldKey.StandardLaunchDuration,
    label: i18n.t('使用时长（秒）'),
    attach: false,
    hidden: false,
    formItemProps: {
      style: { maxWidth: DEFAULT_STANDARD_FIELD_MAX_WIDTH },
    },
    componentProps: {
      type: 'tag' as ComponentType,
      operatorOptions: NUMBER_MATCH_OPTIONS,
      placeholder: i18n.t('请输入'),
    },
    fieldProps: {
      fieldScope: null,
      filterName: CommonFieldKey.LaunchDuration,
    },
    group: TdsFromNextGroup.ContextInfo,
  },

  [StandardCommonFieldKey.ClientIdentify]: {
    name: StandardCommonFieldKey.ClientIdentify,
    label: i18n.t('消息ID'),
    attach: false,
    hidden: false,
    formItemProps: {
      style: { maxWidth: DEFAULT_STANDARD_FIELD_MAX_WIDTH },
    },
    componentProps: {
      type: 'tag' as ComponentType,
      operatorOptions: CHARACTER_MATCH_OPTIONS,
      placeholder: i18n.t('请输入'),
    },
    fieldProps: {
      fieldScope: null,
      filterName: CommonFieldKey.ClientIdentify,
      // valueTransform: (value: any) => ({
      //   clientIdentify: value?.conditions?.[0]?.values?.[0],
      // }),
    },
    batchEditMode: true,
    batchEditTips: stringFilterTips,
    group: TdsFromNextGroup.ExceptionInfo,
  },

  [StandardCommonFieldKey.Feature]: {
    name: StandardCommonFieldKey.Feature,
    label: 'Issue ID',
    attach: false,
    hidden: false,
    formItemProps: {
      style: { maxWidth: DEFAULT_STANDARD_FIELD_MAX_WIDTH },
    },
    componentProps: {
      type: 'tag' as ComponentType,
      operatorOptions: CHARACTER_MATCH_OPTIONS,
      placeholder: i18n.t('请输入'),
    },
    fieldProps: {
      fieldScope: null,
      filterName: CommonFieldKey.Feature,
    },
    batchEditMode: true,
    batchEditTips: stringFilterTips,
    group: TdsFromNextGroup.ExceptionInfo,
  },

  [StandardCommonFieldKey.FuncName]: {
    name: StandardCommonFieldKey.FuncName,
    label: i18n.t('问题特征'),
    attach: false,
    hidden: false,
    formItemProps: {
      style: { maxWidth: DEFAULT_STANDARD_FIELD_MAX_WIDTH },
    },
    componentProps: {
      type: 'tag' as ComponentType,
      operatorOptions: CHARACTER_MATCH_OPTIONS,
      defaultOperator: MatchMode.MATCH,
      placeholder: i18n.t('请输入'),
    },
    fieldProps: {
      fieldScope: null,
      filterName: CommonFieldKey.FuncName,
    },
    group: TdsFromNextGroup.ExceptionInfo,
  },

  [StandardCommonFieldKey.Stack]: {
    name: StandardCommonFieldKey.Stack,
    label: i18n.t('出错堆栈'),
    attach: false,
    hidden: false,
    formItemProps: {
      style: { maxWidth: DEFAULT_STANDARD_FIELD_MAX_WIDTH },
    },
    componentProps: {
      type: 'tag' as ComponentType,
      operatorOptions: STACK_CHARACTER_MATCH_OPTIONS,
      placeholder: i18n.t('请输入'),
    },
    fieldProps: {
      fieldScope: null,
      filterName: CommonFieldKey.TranslatedStacks,
    },
    group: TdsFromNextGroup.ExceptionInfo,
  },

  standard_leak_bytes: {
    name: 'standard_leak_bytes',
    label: i18n.t('最大泄漏内存（KB）'),
    attach: false,
    hidden: false,
    formItemProps: {
      style: { maxWidth: '250px' },
    },
    componentProps: {
      type: 'tag' as ComponentType,
      operatorOptions: NUMBER_MATCH_OPTIONS,
      placeholder: i18n.t('请输入'),
    },
    fieldProps: {
      fieldScope: null,
      filterName: 'memory_leak_size',
      filterTransform: (
        fieldKey: string,
        fieldValue: any,
      ) => tagFilterValue2ScaledFilter(fieldKey, fieldValue, UNIT_SCALE.KB_TO_BYTES),
    },
    group: TdsFromNextGroup.ExceptionInfo,
  },

  [StandardCommonFieldKey.UserCustom]: {
    name: StandardCommonFieldKey.UserCustom,
    label: i18n.t('自定义维度'),
    attach: false,
    hidden: false,
    componentProps: {
      type: 'customFields' as ComponentType,
      customType: 'customFields' as const,
    },
    fieldProps: {
      filterName: CommonFieldKey.UserCustom,
      filterTransform: (_fieldKey: string, fieldValue: any) => fieldValue?.map((item: any) => ({
        name: CommonFieldKey.UserCustom,
        operator: item.operator,
        values: item.values || [],
        key: item.key,
        type: item.type,
      })) ?? [],
    },
    group: TdsFromNextGroup.Custom,
  },

  [StandardCommonFieldKey.BizExtendInfo]: {
    name: StandardCommonFieldKey.BizExtendInfo,
    label: i18n.t('自定义字段'),
    attach: false,
    hidden: false,
    componentProps: {
      type: 'customFields' as ComponentType,
      customType: 'customKey' as const,
    },
    fieldProps: {
      filterName: CommonFieldKey.BizExtendInfo,
      filterTransform: (_fieldKey: string, fieldValue: any) => fieldValue?.map((item: any) => ({
        name: CommonFieldKey.BizExtendInfo,
        operator: item.operator,
        values: item.values || [],
        key: item.key,
        type: KeyDataType.STRING,
      })) ?? [],
    },
    group: TdsFromNextGroup.Custom,
  },

  [StandardCommonFieldKey.VmMaxSize]: {
    name: StandardCommonFieldKey.VmMaxSize,
    label: i18n.t('虚拟机最大堆内存(MB)'),
    attach: false,
    hidden: false,
    formItemProps: {
      style: { maxWidth: '350px' },
    },
    componentProps: {
      type: 'tag' as ComponentType,
      operatorOptions: CHARACTER_MATCH_OPTIONS,
      placeholder: i18n.t('请选择'),
    },
    fieldProps: {
      fieldScope: Zone.MemDetail,
      fieldKey: CommonFieldKey.VmMaxSize,
      filterName: CommonFieldKey.VmMaxSize,
      filterTransform: (
        fieldKey: string,
        fieldValue: any,
      ) => tagFilterValue2ScaledFilter(fieldKey, fieldValue, UNIT_SCALE.MB_TO_BYTES),
      optionTransform: (option: any[]) => {
        const seen = new Set<string>();
        return option?.reduce<any[]>((acc, item) => {
          const mb = !isNaN(Number(item.value)) ? String(Math.round(Number(item.value) / 1024 / 1024)) : item.value;
          if (!seen.has(mb)) {
            seen.add(mb);
            acc.push({ ...item, value: mb, label: mb });
          }
          return acc;
        }, []);
      },
    },
    group: TdsFromNextGroup.ContextInfo,
  },

  [StandardCommonFieldKey.LeakObjectType]: {
    name: StandardCommonFieldKey.LeakObjectType,
    label: i18n.t('只显示泄漏个例'),
    attach: false,
    hidden: false,
    formItemProps: {
      style: { maxWidth: DEFAULT_STANDARD_FIELD_MAX_WIDTH },
    },
    componentProps: {
      type: 'tag' as ComponentType,
      operatorOptions: CHARACTER_MATCH_OPTIONS,
      placeholder: i18n.t('请选择'),
      options: [
        { value: 'leak', label: i18n.t('是') },
        { value: '0', label: i18n.t('否') },
      ],
    },
    fieldProps: {
      fieldScope: null,
      filterName: CommonFieldKey.LeakObjectType,
      filterTransform: (_fieldKey: string, fieldValue: any) => {
        const value = fieldValue?.conditions?.[0]?.values?.[0];
        if (!value || value === '0') return [];
        return [{
          name: CommonFieldKey.LeakObjectType,
          values: [value],
          operator: fieldValue?.conditions?.[0]?.operator ?? MatchMode.OR,
        }];
      },
    },
    group: TdsFromNextGroup.Tag,
  },

  [StandardCommonFieldKey.DocCount]: {
    name: StandardCommonFieldKey.DocCount,
    label: i18n.t('上报次数'),
    attach: false,
    hidden: false,
    formItemProps: {
      style: { maxWidth: DEFAULT_STANDARD_FIELD_MAX_WIDTH },
    },
    componentProps: {
      type: 'tag' as ComponentType,
      operatorOptions: NUMBER_MATCH_OPTIONS,
      placeholder: i18n.t('请输入'),
    },
    fieldProps: {
      fieldScope: null,
      filterName: CommonFieldKey.DocCount,
    },
    group: TdsFromNextGroup.ExceptionInfo,
  },

  [StandardCommonFieldKey.QimeiCount]: {
    name: StandardCommonFieldKey.QimeiCount,
    label: i18n.t('设备数'),
    attach: false,
    hidden: false,
    formItemProps: {
      style: { maxWidth: DEFAULT_STANDARD_FIELD_MAX_WIDTH },
    },
    componentProps: {
      type: 'tag' as ComponentType,
      operatorOptions: NUMBER_MATCH_OPTIONS,
      placeholder: i18n.t('请输入'),
    },
    fieldProps: {
      fieldScope: null,
      filterName: CommonFieldKey.QimeiCount,
    },
    group: TdsFromNextGroup.ExceptionInfo,
  },

  [StandardCommonFieldKey.DumpReason]: {
    name: StandardCommonFieldKey.DumpReason,
    label: i18n.t('日志来源'),
    attach: false,
    hidden: false,
    formItemProps: {
      style: { maxWidth: DEFAULT_STANDARD_FIELD_MAX_WIDTH },
    },
    componentProps: {
      type: 'tag' as ComponentType,
      operatorOptions: CHARACTER_MATCH_OPTIONS,
      placeholder: i18n.t('请选择'),
    },
    fieldProps: {
      filterName: CommonFieldKey.DumpReason,
    },
    group: TdsFromNextGroup.ExceptionInfo,
  },

  // ── 网络诊断拨测任务 ──
  net_task_keyword: {
    name: 'net_task_keyword',
    label: i18n.t('关键词'),
    attach: false,
    hidden: false,
    formItemProps: {
      style: { maxWidth: DEFAULT_STANDARD_FIELD_MAX_WIDTH },
    },
    componentProps: {
      type: 'tag' as ComponentType,
      operatorOptions: TEXT_MATCH_OPTIONS,
      placeholder: i18n.t('任务名称/ID/创建人/目标'),
      singleSelect: true,
    },
    fieldProps: {
      fieldScope: null,
      filterName: 'keyword',
    },
    group: TdsFromNextGroup.Custom,
  },

  net_task_status: {
    name: 'net_task_status',
    label: i18n.t('状态'),
    attach: false,
    hidden: false,
    formItemProps: {
      style: { maxWidth: DEFAULT_STANDARD_FIELD_MAX_WIDTH },
    },
    componentProps: {
      singleSelect: true,
      mode: 'multiple',
      type: 'tag' as ComponentType,
      operatorOptions: TEXT_MATCH_OPTIONS,
      placeholder: i18n.t('请选择'),
      options: [
        { value: 1, label: i18n.t('运行中') },
        { value: 2, label: i18n.t('已停止') },
        { value: 3, label: i18n.t('已删除') },
      ],
    },
    fieldProps: {
      fieldScope: null,
      filterName: 'filterStatus',
    },
    group: TdsFromNextGroup.Custom,
  },

  net_task_diagnose_type: {
    name: 'net_task_diagnose_type',
    label: i18n.t('拨测类型'),
    attach: false,
    hidden: false,
    formItemProps: {
      style: { maxWidth: DEFAULT_STANDARD_FIELD_MAX_WIDTH },
    },
    componentProps: {
      type: 'tag' as ComponentType,
      operatorOptions: TEXT_MATCH_OPTIONS,
      placeholder: i18n.t('请选择'),
      // 单选模式：值只能选一个，且隐藏「添加」按钮（仅允许一条筛选条件）
      singleSelect: true,
      options: [
        { value: 1, label: 'HTTP/HTTPS' },
        { value: 2, label: 'TCP' },
        { value: 3, label: 'Ping/ICMP' },
        { value: 4, label: 'UDP' },
        { value: 5, label: 'Traceroute' },
      ],
    },
    fieldProps: {
      fieldScope: null,
      filterName: 'filterDiagnoseType',
    },
    group: TdsFromNextGroup.Custom,
  },

  standard_client_country: {
    name: 'standard_client_country',
    label: i18n.t('客户端国家'),
    attach: false,
    hidden: false,
    formItemProps: {
      style: { maxWidth: DEFAULT_STANDARD_FIELD_MAX_WIDTH },
    },
    componentProps: {
      type: 'tag' as ComponentType,
      operatorOptions: CHARACTER_MATCH_OPTIONS,
      placeholder: i18n.t('请选择'),
    },
    fieldProps: {
      fieldScope: Zone.NetworkDiagnose,
      fieldKey: 'client_country',
      filterName: 'client_country',
    },
    group: TdsFromNextGroup.ApplicationInfo,
  },
  standard_client_province: {
    name: 'standard_client_province',
    label: i18n.t('客户端省份'),
    attach: false,
    hidden: false,
    formItemProps: {
      style: { maxWidth: DEFAULT_STANDARD_FIELD_MAX_WIDTH },
    },
    componentProps: {
      type: 'tag' as ComponentType,
      operatorOptions: CHARACTER_MATCH_OPTIONS,
      placeholder: i18n.t('请选择'),
    },
    fieldProps: {
      fieldScope: Zone.NetworkDiagnose,
      fieldKey: 'client_province',
      filterName: 'client_province',
    },
    group: TdsFromNextGroup.ApplicationInfo,
  },


  standard_client_city: {
    name: 'standard_client_city',
    label: i18n.t('客户端城市'),
    attach: false,
    hidden: false,
    formItemProps: {
      style: { maxWidth: DEFAULT_STANDARD_FIELD_MAX_WIDTH },
    },
    componentProps: {
      type: 'tag' as ComponentType,
      operatorOptions: CHARACTER_MATCH_OPTIONS,
      placeholder: i18n.t('请选择'),
    },
    fieldProps: {
      fieldScope: Zone.NetworkDiagnose,
      fieldKey: 'client_city',
      filterName: 'client_city',
    },
    group: TdsFromNextGroup.ApplicationInfo,
  },

  standard_client_isp: {
    name: 'standard_client_isp',
    label: i18n.t('客户端运营商'),
    attach: false,
    hidden: false,
    formItemProps: {
      style: { maxWidth: DEFAULT_STANDARD_FIELD_MAX_WIDTH },
    },
    componentProps: {
      type: 'tag' as ComponentType,
      operatorOptions: CHARACTER_MATCH_OPTIONS,
      placeholder: i18n.t('请选择'),
    },
    fieldProps: {
      fieldScope: Zone.NetworkDiagnose,
      fieldKey: 'client_isp',
      filterName: 'client_isp',
    },
    group: TdsFromNextGroup.ApplicationInfo,
  },

  standard_server_country: {
    name: 'standard_server_country',
    label: i18n.t('服务端国家'),
    attach: false,
    hidden: false,
    formItemProps: {
      style: { maxWidth: DEFAULT_STANDARD_FIELD_MAX_WIDTH },
    },
    componentProps: {
      type: 'tag' as ComponentType,
      operatorOptions: CHARACTER_MATCH_OPTIONS,
      placeholder: i18n.t('请选择'),
    },
    fieldProps: {
      fieldScope: Zone.NetworkDiagnose,
      fieldKey: 'server_country',
      filterName: 'server_country',
    },
    group: TdsFromNextGroup.ApplicationInfo,
  },

  standard_server_province: {
    name: 'standard_server_province',
    label: i18n.t('服务端省份'),
    attach: false,
    hidden: false,
    formItemProps: {
      style: { maxWidth: DEFAULT_STANDARD_FIELD_MAX_WIDTH },
    },
    componentProps: {
      type: 'tag' as ComponentType,
      operatorOptions: CHARACTER_MATCH_OPTIONS,
      placeholder: i18n.t('请选择'),
    },
    fieldProps: {
      fieldScope: Zone.NetworkDiagnose,
      fieldKey: 'server_province',
      filterName: 'server_province',
    },
    group: TdsFromNextGroup.ApplicationInfo,
  },

  standard_server_city: {
    name: 'standard_server_city',
    label: i18n.t('服务端城市'),
    attach: false,
    hidden: false,
    formItemProps: {
      style: { maxWidth: DEFAULT_STANDARD_FIELD_MAX_WIDTH },
    },
    componentProps: {
      type: 'tag' as ComponentType,
      operatorOptions: CHARACTER_MATCH_OPTIONS,
      placeholder: i18n.t('请选择'),
    },
    fieldProps: {
      fieldScope: Zone.NetworkDiagnose,
      fieldKey: 'server_city',
      filterName: 'server_city',
    },
    group: TdsFromNextGroup.ApplicationInfo,
  },

  standard_server_isp: {
    name: 'standard_server_isp',
    label: i18n.t('服务端运营商'),
    attach: false,
    hidden: false,
    formItemProps: {
      style: { maxWidth: DEFAULT_STANDARD_FIELD_MAX_WIDTH },
    },
    componentProps: {
      type: 'tag' as ComponentType,
      operatorOptions: CHARACTER_MATCH_OPTIONS,
      placeholder: i18n.t('请选择'),
    },
    fieldProps: {
      fieldScope: Zone.NetworkDiagnose,
      fieldKey: 'server_isp',
      filterName: 'server_isp',
    },
    group: TdsFromNextGroup.ApplicationInfo,
  },
};

export const SCHEMA_STANDARD_FIELD_POOL = RAW_SCHEMA_STANDARD_FIELD_POOL;

